/**
 * Documentos en la vista previa: la carpeta de SharePoint de cada venta.
 *
 * Las mismas dos órdenes que siembra Ventas (`preview-ventas.ts`): S00087, cuyo
 * trato tiene carpeta, y S00099, cuyo trato todavía no. La carpeta se puede
 * navegar, y crear, subir y borrar cambian el árbol en memoria hasta recargar.
 */

interface Elemento {
  id: string;
  nombre: string;
  es_carpeta: boolean;
  elementos: number | null;
  tamano: number | null;
  modificado: string | null;
  modificado_por: string | null;
  web_url: string | null;
  descarga: string | null;
  padre: string;
}

const SP = 'https://coreconsultingsa.sharepoint.com/sites/Mercado-Oportunidades/Documentos%20compartidos/Oportunidades/Vippinn%20ARG';
const TRATO = { id: 'TRATO-118', nombre: '118 - Venta inicial CER', cliente: 'Vippinn ARG' };

const archivo = (id: string, nombre: string, tamano: number, padre: string, por = 'Silvina Álvarez'): Elemento => ({
  id, nombre, es_carpeta: false, elementos: null, tamano,
  modificado: '2026-09-18T14:32:00Z', modificado_por: por,
  web_url: `${SP}/${encodeURIComponent(nombre)}`, descarga: `${SP}/${encodeURIComponent(nombre)}?descargar=1`, padre
});

const carpeta = (id: string, nombre: string, padre: string): Elemento => ({
  id, nombre, es_carpeta: true, elementos: 0, tamano: null,
  modificado: '2026-09-10T11:00:00Z', modificado_por: 'Matías Salto',
  web_url: `${SP}/${encodeURIComponent(nombre)}`, descarga: null, padre
});

let arbol: Elemento[] = [
  carpeta('ACTAS', 'Actas de reunión', TRATO.id),
  carpeta('ENTREGABLES', 'Entregables', TRATO.id),
  archivo('PROP', 'Propuesta comercial CER v3.pdf', 2_480_113, TRATO.id),
  archivo('OC', 'Orden de compra 4500123.pdf', 318_204, TRATO.id, 'Matías Salto'),
  archivo('KICK', 'Acta kickoff 2026-09-03.docx', 41_877, 'ACTAS'),
  archivo('MIN', 'Minuta seguimiento semana 2.docx', 38_102, 'ACTAS', 'Valentina Merlo')
];
let siguiente = 1;

function hijos(id: string): Elemento[] {
  return arbol
    .filter((e) => e.padre === id)
    .map((e) => (e.es_carpeta ? { ...e, elementos: arbol.filter((x) => x.padre === e.id).length } : e))
    .sort((a, b) => (a.es_carpeta !== b.es_carpeta ? (a.es_carpeta ? -1 : 1) : a.nombre.localeCompare(b.nombre, 'es')));
}

function estado(carpetaId: string | null): unknown {
  const actual = carpetaId ? arbol.find((e) => e.id === carpetaId && e.es_carpeta) : null;
  const migas = [{ id: TRATO.id, nombre: TRATO.nombre }];

  if (actual) migas.push({ id: actual.id, nombre: actual.nombre });

  return {
    success: true,
    perdida: false,
    carpeta: { id: TRATO.id, nombre: TRATO.nombre, web_url: SP, cliente: TRATO.cliente },
    actual: actual
      ? { id: actual.id, nombre: actual.nombre, web_url: actual.web_url }
      : { id: TRATO.id, nombre: TRATO.nombre, web_url: SP },
    migas,
    elementos: hijos(actual?.id ?? TRATO.id)
  };
}

export function respuestaDocumentos(
  metodo: string,
  ruta: string,
  params: URLSearchParams,
  cuerpo: unknown
): unknown | null {
  if (metodo === 'GET' && /\/api\/project\/[^/]+\/documentos\/?$/.test(ruta)) {
    return {
      success: true,
      disponible: true,
      puede_editar: true,
      max_mb: 250,
      repos: [
        { orden_id: 1, orden: 'S00087', fecha: '2026-09-01', trato_codigo: '118', trato_nombre: 'Venta inicial', tiene_carpeta: true },
        { orden_id: 2, orden: 'S00099', fecha: '2026-09-02', trato_codigo: '144', trato_nombre: 'Ampliación', tiene_carpeta: false }
      ]
    };
  }

  const m = ruta.match(/\/api\/project\/[^/]+\/documentos\/(\d+)(\/.*)?$/);
  if (!m) return null;

  const resto = m[2] ?? '';
  const carpetaPedida = params.get('carpeta');

  if (metodo === 'GET' && (resto === '' || resto === '/')) {
    return estado(carpetaPedida);
  }

  if (metodo === 'POST' && resto === '/carpetas') {
    const datos = (cuerpo ?? {}) as { nombre?: string; carpeta?: string | null };
    const padre = datos.carpeta || TRATO.id;
    const nombre = (datos.nombre ?? '').trim();

    if (arbol.some((e) => e.padre === padre && e.nombre.toLowerCase() === nombre.toLowerCase())) {
      return { success: false, message: `Ya hay una carpeta «${nombre}» acá.` };
    }

    arbol = [...arbol, { ...carpeta(`NUEVA-${siguiente++}`, nombre, padre), modificado: new Date().toISOString(), modificado_por: 'Silvina Álvarez' }];
    return estado(datos.carpeta || null);
  }

  if (metodo === 'POST' && resto === '/archivos') {
    const subido = cuerpo instanceof FormData ? (cuerpo.get('archivo') as File | null) : null;
    const nuevo = {
      ...archivo(`SUBIDO-${siguiente++}`, subido?.name ?? 'archivo.pdf', subido?.size ?? 0, carpetaPedida || TRATO.id),
      modificado: new Date().toISOString()
    };
    arbol = [...arbol, nuevo];
    const { padre: _padre, ...elemento } = nuevo;
    return { success: true, elemento };
  }

  const borrar = resto.match(/^\/elementos\/([^/]+)$/);
  if (metodo === 'DELETE' && borrar) {
    const id = decodeURIComponent(borrar[1]);
    const fuera = new Set([id]);
    // Con lo que tenga adentro, como en SharePoint
    let crecio = true;
    while (crecio) {
      const antes = fuera.size;
      arbol.filter((e) => fuera.has(e.padre)).forEach((e) => fuera.add(e.id));
      crecio = fuera.size > antes;
    }
    arbol = arbol.filter((e) => !fuera.has(e.id));
    return estado(carpetaPedida);
  }

  return null;
}
